import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronRightIcon, PlayCircleIcon, UserGroupIcon, TrophyIcon, SpeakerWaveIcon, MicrophoneIcon, ArrowLeftIcon, ArrowRightIcon, CalendarDaysIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { REDDIT_EVENTS } from '@/lib/analytics-config';

const OTCampaignLanding = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Track OT Campaign page view in Reddit
    if (typeof window !== 'undefined' && window.rdt) {
      window.rdt('track', 'ViewContent', {
        content_type: 'ot_campaign',
        user_type: 'professional',
        profession: 'Occupational Therapist',
        page_path: window.location.pathname,
        source: 'page_load'
      });
    }

    // Load JotForm embed handler script
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.onload = () => {
      // Initialize the embed handler after script loads
      if (window.jotformEmbedHandler) {
        window.jotformEmbedHandler("iframe[id='JotFormIFrame-01981f5b0f687195a04b012060358e6da04c']", "https://www.jotform.com");
      }
      setIsLoading(false);
    };
    document.head.appendChild(script);
    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  return <>
      <SEO
        title="OT Network Presentation - Partnership"
        description="Discover how joining the SeniorThrive OT Network can expand your practice with qualified referrals and innovative home safety technology."
        keywords="occupational therapy, OT network, home safety, referrals, partnership, SeniorThrive"
        canonical="https://seniorthrive.com/occupational-therapists/presentation"
      />

      {/* Compact Hero Section */}
      <section className="relative py-8 overflow-hidden bg-gradient-to-br from-primary/10 via-background to-accent/10">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-10 w-24 h-24 border border-primary rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-16 h-16 border border-primary rounded-full animate-pulse delay-1000"></div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main headline */}
            <h1 className="text-3xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              Expand Your
              <span className="text-primary block">OT Practice</span>
              <span className="text-muted-foreground text-xl lg:text-2xl block mt-2">
                With Technology & Qualified Referrals
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-lg text-muted-foreground mb-6 max-w-3xl mx-auto">
              Interactive presentation with our smart agent to discover qualified referrals with 
              <span className="text-primary font-semibold"> rich home-safety data</span>
            </p>

            {/* Presentation Status */}
            <Badge className="bg-primary/10 text-primary border-primary/20">
              <PlayCircleIcon className="w-4 h-4 mr-2" />
              8-10 Minute Interactive Experience
            </Badge>
          </div>
        </div>
      </section>

      {/* Presentation Instructions - Prominent Box */}
      <section className="bg-gradient-to-r from-primary/5 to-accent/5 border-y border-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background/95 backdrop-blur-sm rounded-xl p-6 border border-primary/20 shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Interactive Presentation Guide</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <SpeakerWaveIcon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Turn Your Volume On</p>
                      <p className="text-muted-foreground">Best experience with audio enabled</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MicrophoneIcon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Use Your Microphone</p>
                      <p className="text-muted-foreground">Ask questions - our smart agent is ready to chat!</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex gap-1 mt-0.5 flex-shrink-0">
                      <ArrowLeftIcon className="w-4 h-4 text-primary" />
                      <ArrowRightIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Navigate Easily</p>
                      <p className="text-muted-foreground">Use bottom controls to move or jump to specific slides</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex gap-1 mt-0.5 flex-shrink-0 w-9 justify-center">
                      <CalendarDaysIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Book a Live Meeting</p>
                      <p className="text-muted-foreground">Want to talk to a real person? <a href="https://app.apollo.io/#/meet/inbound-router/5j1-cvy-2x3" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">Book a meeting now</a>.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
                <p className="text-sm text-primary font-medium text-center">🤖 This is just you and our smart agent named Eden - feel free to explore and ask questions!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section className="bg-background">
        <div className="mx-auto">
          <div className="w-full">
            {/* Loading State */}
            {isLoading && <div className="bg-muted/30 rounded-lg p-8 text-center mb-2">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your interactive presentation...</p>
              </div>}

            {/* JotForm Presentation Embed */}
            <div className={`overflow-hidden ${isLoading ? 'hidden' : 'block'}`}>
            <iframe id="JotFormIFrame-01981f5b0f687195a04b012060358e6da04c" title="Eden: Ocu" onLoad={() => {
              setIsLoading(false);
            }} allowTransparency={true} allow="geolocation; microphone; camera; fullscreen" src="https://agent.jotform.com/01981f5b0f687195a04b012060358e6da04c/presentation/01982e83105d79d78520823b70dc20829881?embedMode=iframe&background=1&shadow=1&platform=presentationagent" frameBorder="0" style={{
              minWidth: '100%',
              maxWidth: '100%',
              height: '688px',
              border: 'none',
              width: '100%'
            }} scrolling="no">
              </iframe>
            </div>

            {/* Compact Fallback Content */}
            <div className="mt-2 p-4 bg-muted/20 rounded-lg border border-muted">
              <h3 className="text-lg font-semibold mb-2">Can't view the presentation?</h3>
              <p className="text-sm text-muted-foreground mb-3">
                You'll learn: Smart home safety identification • Rich referral data • Practice integration • Success stories • Simple joining process
              </p>
              <Button asChild size="sm">
                <Link to="/occupational-therapists">
                  Learn More About Joining
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Compact Next Steps Section */}
      <section className="py-12 bg-gradient-to-r from-primary to-primary-glow text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Join the Network?
            </h2>
            <p className="text-lg mb-6 text-white/90">
              Expand your practice with qualified referrals and innovative technology.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-6 py-3">
                <Link to="/occupational-therapists#application-form">
                  Apply to Join Network
                  <ChevronRightIcon className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-lg px-6 py-3 bg-white/20 text-white border-2 border-white hover:bg-white hover:text-primary">
                <Link to="/contact">
                  Talk to Partner Success
                </Link>
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-white/20">
              <p className="text-white/80 text-sm">
                Questions? Email us at{' '}
                <a href="mailto:pros@seniorthrive.com" className="text-white underline hover:text-white/80">
                  pros@seniorthrive.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>;
};
export default OTCampaignLanding;